import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { logoutUser } from '../lib/firebaseServices'
import { User, LogOut, ShoppingBag, ChevronDown } from 'lucide-react'
import LanguageSwitcher from './LanguageSwitcher'
import { useAuth } from '../contexts/AuthContext'

export default function Header() {
  const { t } = useTranslation()
  const { currentUser, userRoleData, loading } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const navigate = useNavigate()

  // Use userRoleData for display name
  const userData = userRoleData

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu-wrapper')) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  const handleLogout = async () => {
    try {
      await logoutUser()
      setShowUserMenu(false)
      navigate('/')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <Link to="/" className="logo">
            🏝️ DelyTour
          </Link>

          <button
            className="menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>

          <nav className={`nav ${menuOpen ? 'open' : ''}`}>
            <Link to="/tours" className="nav-link" onClick={() => setMenuOpen(false)}>{t('header.menu.tours')}</Link>
            <Link to="/blog" className="nav-link" onClick={() => setMenuOpen(false)}>{t('header.menu.blog')}</Link>
            <Link to="/destinations" className="nav-link" onClick={() => setMenuOpen(false)}>{t('header.menu.destinations')}</Link>
            <Link to="/about" className="nav-link" onClick={() => setMenuOpen(false)}>{t('header.menu.about')}</Link>
            <Link to="/contact" className="nav-link" onClick={() => setMenuOpen(false)}>{t('header.menu.contact')}</Link>

            {/* Language Switcher */}
            <div className="language-switcher-wrapper">
              <LanguageSwitcher />
            </div>

            {!loading && currentUser ? (
              <div className="user-menu-wrapper">
                <button
                  className="user-menu-button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <User className="w-5 h-5" />
                  <span>{t('header.user.hello')}, {userData?.fullName || userData?.name || t('header.user.guest')}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showUserMenu && (
                  <div className="user-dropdown">
                    <Link to="/profile" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                      <User className="w-4 h-4" />
                      <span>{t('header.user.myAccount')}</span>
                    </Link>
                    <Link to="/my-bookings" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                      <ShoppingBag className="w-4 h-4" />
                      <span>{t('header.user.myBookings')}</span>
                    </Link>
                    <button onClick={handleLogout} className="dropdown-item">
                      <LogOut className="w-4 h-4" />
                      <span>{t('header.user.logout')}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="nav-link btn-login" onClick={() => setMenuOpen(false)}>{t('common.login')}</Link>
            )}
          </nav>
        </div>
      </div>

      <style>{`
        .header {
          background: white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 70px;
        }

        .logo {
          font-size: 1.5rem;
          font-weight: 700;
          text-decoration: none;
          color: var(--color-primary);
        }

        .menu-toggle {
          display: none;
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          padding: 8px;
        }

        .nav {
          display: flex;
          align-items: center;
          gap: 32px;
        }

        .nav-link {
          text-decoration: none;
          color: #374151;
          font-weight: 500;
          transition: color 0.2s;
        }

        .nav-link:hover {
          color: var(--color-primary);
        }

        .btn-login {
          padding: 8px 20px;
          background: linear-gradient(135deg, var(--color-button-gradient-from) 0%, var(--color-button-gradient-to) 100%) !important;
          color: white !important;
          border-radius: 8px;
        }

        .btn-login:hover {
          opacity: 0.9;
          color: white !important;
          transform: translateY(-1px);
        }

        .user-menu-wrapper {
          position: relative;
          z-index: 50;
        }

        .user-menu-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: linear-gradient(135deg, var(--color-button-gradient-from) 0%, var(--color-button-gradient-to) 100%) !important;
          color: white !important;
          border: none;
          border-radius: 25px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: transform 0.2s;
          white-space: nowrap;
        }

        .user-menu-button:hover {
          transform: scale(1.05);
          opacity: 0.9;
        }

        .user-dropdown {
          position: absolute;
          top: calc(100% + 12px);
          right: 0;
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
          min-width: 220px;
          overflow: visible;
          z-index: 9999;
          border: 1px solid rgba(0, 0, 0, 0.1);
        }
        
        .user-dropdown::before {
          content: '';
          position: absolute;
          top: -8px;
          right: 20px;
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 8px solid white;
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          color: #374151;
          text-decoration: none;
          transition: background 0.2s;
          border: none;
          width: 100%;
          text-align: left;
          background: none;
          cursor: pointer;
          font-size: 14px;
        }

        .dropdown-item:hover {
          background: #f3f4f6;
        }

        .dropdown-item:not(:last-child) {
          border-bottom: 1px solid #e5e7eb;
        }

        @media (max-width: 768px) {
          .menu-toggle {
            display: block;
          }

          .nav {
            position: absolute;
            top: 70px;
            left: 50%;
            transform: translateX(-50%);
            width: 85%;
            max-width: 320px;
            background: white;
            flex-direction: column;
            padding: 12px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            border-radius: 8px;
            display: none;
          }

          .nav.open {
            display: flex;
          }

          .nav-link {
            width: 100%;
            text-align: center;
            padding: 10px 12px;
            font-size: 14px;
          }
        }
      `}</style>
    </header>
  )
}

